import { api, apiErrorMessage, oneSpec, useHydrated } from '@bemor/web-kit';
import { Button, Card, CardContent, PageHeader } from '@bemor/web-kit/ui';
import { useEffect, useState } from 'react';

import { useOne, useWrite } from '@/lib/queries';

function urlBase64ToUint8Array(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Browser push for this device: the service worker in public/sw.js shows what Delivery sends to the subscription. */
/** The reads of `/settings/push` (BM-46 §2.0). */
export const pushReads = () => ({
    publicKey: oneSpec('/staff/notifications/push-subscriptions/public-key'),
});

export function PushPage() {
    const key = useOne<{ public_key: string | null }>(
        pushReads().publicKey.path,
    );
    const [current, setCurrent] = useState<PushSubscription | null>(null);
    // BM-109 (D5): decided after hydration, so the server and the first client render agree (React error #418 otherwise)
    const hydrated = useHydrated();
    const supported =
        hydrated && 'serviceWorker' in navigator && 'PushManager' in window;
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (!supported) return;
        navigator.serviceWorker
            .register('/sw.js')
            .then((reg) => reg.pushManager.getSubscription())
            .then(setCurrent)
            .catch(() => undefined);
    }, [supported]);
    const subscribe = useWrite(
        async (publicKey: string) => {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(
                    publicKey,
                ) as BufferSource,
            });
            const json = sub.toJSON();
            await api().post('/staff/notifications/push-subscriptions', {
                endpoint: json.endpoint,
                keys: json.keys,
            });
            setCurrent(sub);
        },
        ['/staff/notifications/push-subscriptions'],
    );
    const unsubscribe = useWrite(
        async (sub: PushSubscription) => {
            await api().delete('/staff/notifications/push-subscriptions', {
                params: { endpoint: sub.endpoint },
            });
            await sub.unsubscribe();
            setCurrent(null);
        },
        ['/staff/notifications/push-subscriptions'],
    );
    const publicKey = key.data?.data.public_key ?? null;
    return (
        <div>
            <PageHeader
                title="Push on this device"
                meta="Urgent alerts and approvals waiting for you reach this browser even when the tab is closed."
            />
            <Card className="max-w-lg">
                <CardContent className="space-y-3 pt-6 text-sm">
                    {error ? (
                        <p role="alert" className="text-destructive">
                            {error}
                        </p>
                    ) : null}
                    {!supported ? (
                        <p className="text-muted-foreground">
                            This browser does not support web push.
                        </p>
                    ) : key.data && publicKey === null ? (
                        <p
                            className="text-muted-foreground"
                            data-testid="push-unconfigured"
                        >
                            Push is not configured for this environment yet (no
                            VAPID key on Delivery).
                        </p>
                    ) : current ? (
                        <>
                            <p>This device receives push notifications.</p>
                            <Button
                                variant="outline"
                                onClick={() =>
                                    unsubscribe.mutate(current, {
                                        onError: (e) =>
                                            setError(apiErrorMessage(e)),
                                    })
                                }
                                disabled={unsubscribe.isPending}
                            >
                                Turn off on this device
                            </Button>
                        </>
                    ) : (
                        <>
                            <p>Push is off on this device.</p>
                            <Button
                                onClick={() =>
                                    publicKey &&
                                    subscribe.mutate(publicKey, {
                                        onError: (e) =>
                                            setError(apiErrorMessage(e)),
                                    })
                                }
                                disabled={!publicKey || subscribe.isPending}
                            >
                                Turn on
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
